import { services } from '@config/services';
import { catalogApi } from './base';

export interface ShippingGroup {
  id: string;
  merchantId: string;
  name: string;
  rank: number;
  productCount?: number;
  isDefault?: boolean;
}

export interface ListShippingGroupsResponse {
  groups: ShippingGroup[];
}

export interface GetShippingGroupResponse {
  group: ShippingGroup;
}

export interface CreateShippingGroupInput {
  merchantId: string;
  name: string;
}

export interface UpdateShippingGroupInput {
  id: string;
  name: string;
}

export interface SetVariantShippingGroupInput {
  merchantCatalogId: string;
  skus: string[];
  shippingGroupId?: string | null;
}

export interface SetVariantShippingGroupResponse {
  updatedCount: number;
  /** Serialized Gooten.Shared.Web Error: unknown SKUs arrive as a string[] attemptedValue. */
  errors?: { errorMessage?: string; propertyName?: string | null; attemptedValue?: unknown }[];
}

export interface GetShippingGroupsBySkusInput {
  merchantId: string;
  skus: string[];
}

export interface GetShippingGroupsBySkusResponse {
  groups: Record<string, string | null>;
}

export interface ReorderShippingGroupsInput {
  merchantId: string;
  orderedGroupIds: string[];
}

export interface ReorderShippingGroupsResponse {
  groups: ShippingGroup[];
}

export const shippingGroupsApi = catalogApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /catalog/v1/shippingGroups?merchantId= */
    listShippingGroups: builder.query<ListShippingGroupsResponse, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `${services.catalog}/v1/shippingGroups?merchantId=${encodeURIComponent(merchantId)}`,
        method: 'GET',
      }),
      providesTags: (result) => [
        { type: 'ShippingGroupList' as const, id: 'LIST' },
        ...(result?.groups ?? []).map((g) => ({ type: 'ShippingGroup' as const, id: g.id })),
      ],
    }),

    /** GET /catalog/v1/shippingGroups/{id} */
    getShippingGroup: builder.query<GetShippingGroupResponse, { id: string }>({
      query: ({ id }) => ({
        url: `${services.catalog}/v1/shippingGroups/${encodeURIComponent(id)}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { id }) => [{ type: 'ShippingGroup', id }],
    }),

    /** POST /catalog/v1/shippingGroups */
    createShippingGroup: builder.mutation<ShippingGroup, CreateShippingGroupInput>({
      query: (body) => ({
        url: `${services.catalog}/v1/shippingGroups`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ShippingGroupList', id: 'LIST' }],
    }),

    /** PUT /catalog/v1/shippingGroups/{id} */
    updateShippingGroup: builder.mutation<ShippingGroup, UpdateShippingGroupInput>({
      query: ({ id, ...body }) => ({
        url: `${services.catalog}/v1/shippingGroups/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'ShippingGroup', id },
        { type: 'ShippingGroupList', id: 'LIST' },
      ],
    }),

    /** DELETE /catalog/v1/shippingGroups/{id} */
    deleteShippingGroup: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `${services.catalog}/v1/shippingGroups/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'ShippingGroup', id },
        { type: 'ShippingGroupList', id: 'LIST' },
        // The delete moves the group's SKUs to Default in every catalog, and which catalogs
        // is unknown here; a SKU list left cached would show them under a deleted group.
        'CatalogVariant',
      ],
    }),

    /** PATCH /catalog/v1/shippingGroups/variants — null shippingGroupId moves SKUs to the Default group */
    setVariantShippingGroup: builder.mutation<SetVariantShippingGroupResponse, SetVariantShippingGroupInput>({
      query: (body) => ({
        url: `${services.catalog}/v1/shippingGroups/variants`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { merchantCatalogId }) => [
        { type: 'CatalogVariant', id: `LIST:${merchantCatalogId}` },
      ],
    }),

    /** POST /catalog/v1/shippingGroups/bySkus */
    getShippingGroupsBySkus: builder.mutation<GetShippingGroupsBySkusResponse, GetShippingGroupsBySkusInput>({
      query: (body) => ({
        url: `${services.catalog}/v1/shippingGroups/bySkus`,
        method: 'POST',
        body,
      }),
    }),

    /** PUT /catalog/v1/shippingGroups/reorder */
    reorderShippingGroups: builder.mutation<ReorderShippingGroupsResponse, ReorderShippingGroupsInput>({
      query: (body) => ({
        url: `${services.catalog}/v1/shippingGroups/reorder`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result) => [
        { type: 'ShippingGroupList' as const, id: 'LIST' },
        ...(result?.groups ?? []).map((g) => ({ type: 'ShippingGroup' as const, id: g.id })),
      ],
    }),
  }),
});

export const {
  useListShippingGroupsQuery,
  useLazyListShippingGroupsQuery,
  useGetShippingGroupQuery,
  useCreateShippingGroupMutation,
  useUpdateShippingGroupMutation,
  useDeleteShippingGroupMutation,
  useSetVariantShippingGroupMutation,
  useGetShippingGroupsBySkusMutation,
  useReorderShippingGroupsMutation,
} = shippingGroupsApi;
